import { ActionIcon, Button, Loader, Tooltip } from '@mantine/core';
import { IconMicrophone, IconPlayerStopFilled } from '@tabler/icons-react';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  isVoiceRecordingSupported,
  useVoiceRecorder,
  VoiceRecording,
} from 'src/Hooks/Shared/useVoiceRecorder';
import {
  trackVoiceDictationCompleted,
  trackVoiceDictationFailed,
  trackVoiceDictationStarted,
  VoiceDictationFailureReason,
} from 'src/Services/Mixpanel/events';
import { ApiError } from 'src/types/api-error.types';

import styles from './VoiceDictationButton.module.scss';
import { Props } from './VoiceDictationButton.types';

const MAX_DURATION_SECONDS = 120;

const formatElapsed = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const failureReasonFor = (error: unknown): VoiceDictationFailureReason => {
  const response = (error as AxiosError<ApiError>)?.response;
  switch (response?.data?.code) {
    case 'EMPTY_DICTATION':
      return 'empty';
    case 'AUDIO_TOO_LARGE':
      return 'too_large';
    case 'UNSUPPORTED_AUDIO_FORMAT':
      return 'unsupported_format';
    default:
      return response?.status === 429 ? 'too_many' : 'server_error';
  }
};

const VoiceDictationButton = ({ module, transcribe, onTranscript, onError, size = 48 }: Props) => {
  const { t, i18n } = useTranslation();
  const [isTranscribing, setIsTranscribing] = useState(false);

  const errorMessages: Record<VoiceDictationFailureReason, string> = {
    empty: t(
      'voiceDictation.errors.empty',
      "Je n'ai rien entendu. Réessaie en parlant plus près du micro."
    ),
    too_large: t(
      'voiceDictation.errors.tooLarge',
      'La dictée est trop longue. Fais-la en plusieurs fois.'
    ),
    unsupported_format: t(
      'voiceDictation.errors.unsupported',
      "Ce navigateur enregistre dans un format qu'on ne sait pas lire. Essaie avec un autre navigateur."
    ),
    too_many: t(
      'voiceDictation.errors.tooMany',
      'Trop de dictées d’affilée, réessaie dans un instant.'
    ),
    server_error: t(
      'voiceDictation.errors.generic',
      "La dictée n'a pas pu être transcrite. Réessaie dans un instant."
    ),
    permission_denied: t(
      'voiceDictation.errors.denied',
      "L'accès au micro est bloqué. Autorise-le dans les réglages du navigateur (icône à gauche de l'adresse du site), puis réessaie."
    ),
    no_microphone: t(
      'voiceDictation.errors.noMicrophone',
      'Aucun micro n’a été trouvé sur cet appareil.'
    ),
  };

  const fail = (reason: VoiceDictationFailureReason, durationSeconds?: number) => {
    onError(errorMessages[reason]);
    trackVoiceDictationFailed({ module, reason, duration_seconds: durationSeconds });
  };

  // Never logs nor tracks the dictated text itself.
  const onRecorded = async ({ audio, filename, durationSeconds }: VoiceRecording) => {
    if (audio.size === 0) {
      fail('empty', durationSeconds);
      return;
    }
    setIsTranscribing(true);
    try {
      const language = i18n.language?.startsWith('en') ? 'en' : 'fr';
      const text = await transcribe(audio, filename, language);
      onTranscript(text);
      trackVoiceDictationCompleted({ module, duration_seconds: durationSeconds });
    } catch (error) {
      fail(failureReasonFor(error), durationSeconds);
    } finally {
      setIsTranscribing(false);
    }
  };

  const { isRecording, elapsedSeconds, start, stop } = useVoiceRecorder({
    maxDurationSeconds: MAX_DURATION_SECONDS,
    onRecorded,
  });

  // Browser without recording: no button rather than a muted disabled one.
  if (!isVoiceRecordingSupported()) {
    return null;
  }

  const onStart = async () => {
    onError(null);
    try {
      await start();
      trackVoiceDictationStarted({ module });
    } catch (error) {
      const name = (error as DOMException)?.name;
      fail(
        name === 'NotAllowedError' || name === 'SecurityError'
          ? 'permission_denied'
          : 'no_microphone'
      );
    }
  };

  if (isRecording) {
    return (
      <Button
        className={styles.recording}
        h={size}
        color="red"
        onClick={stop}
        leftSection={<IconPlayerStopFilled size={16} />}
        aria-label={t('voiceDictation.stop', 'Arrêter la dictée')}
      >
        {formatElapsed(elapsedSeconds)}
      </Button>
    );
  }

  if (isTranscribing) {
    return (
      <ActionIcon
        size={size}
        variant="outline"
        aria-label={t('voiceDictation.transcribing', 'Transcription en cours…')}
      >
        <Loader size="xs" />
      </ActionIcon>
    );
  }

  return (
    <Tooltip label={t('voiceDictation.start', 'Dicter')}>
      <ActionIcon
        size={size}
        variant="outline"
        onClick={onStart}
        aria-label={t('voiceDictation.start', 'Dicter')}
      >
        <IconMicrophone size={20} />
      </ActionIcon>
    </Tooltip>
  );
};

export default VoiceDictationButton;
